import { useMemo } from 'react';
import { type Resolve } from '@doxyme/types';
import { type SessionParticipant } from '../../webrtc/session';
import {
  getSessionParticipantsMap,
  getAllSessionParticipantsMap,
} from '../../features/session/session.selectors';
import { getAllMembersMap } from '../../features/realtime/realtime.selectors';
import { getChatUserIdFromIdentity } from '../../features/realtime/realtime.helpers';
import { type RealtimeMember } from '../../features/realtime/realtime.types';
import { useAppSelector } from '../useReduxToolkit';
import { useTestPatient } from '../useTestPatient';

export type ProviderViewCallParticipant = Resolve<
  RealtimeMember & SessionParticipant & { chatUserId: string }
>;

export type UseProviderViewCallParticipantsReturnType = {
  /**
   * Map of current call participants, keyed by participant ID.
   */
  callParticipantsMap: Record<string, ProviderViewCallParticipant>;
  /**
   * A list of active participants in the current call.
   * Excludes participants who have already left the call.
   */
  callParticipants: ProviderViewCallParticipant[];
  /**
   * A list of all participants who were ever part of the current call,
   * including those who have already left.
   */
  allCallParticipants: ProviderViewCallParticipant[];
  /**
   * The participant currently sharing their screen, if any.
   */
  screenSharingParticipant?: ProviderViewCallParticipant;
};

export const useProviderViewCallParticipants =
  (): UseProviderViewCallParticipantsReturnType => {
    const sessionParticipantsMap = useAppSelector(getSessionParticipantsMap);
    const allSessionParticipantsMap = useAppSelector(
      getAllSessionParticipantsMap,
    );
    const realtimeMembersMap = useAppSelector(getAllMembersMap);

    const callParticipantsMap = useMemo(
      () => getCallParticipantsMap(sessionParticipantsMap, realtimeMembersMap),
      [sessionParticipantsMap, realtimeMembersMap],
    );

    const { callParticipant: testCallParticipant } = useTestPatient();

    const callParticipantsMapWithTestPatient = useMemo(() => {
      if (!testCallParticipant) return callParticipantsMap;
      return {
        ...callParticipantsMap,
        [testCallParticipant.id]: testCallParticipant,
      };
    }, [callParticipantsMap, testCallParticipant]);

    const callParticipants = Object.values(callParticipantsMapWithTestPatient);

    const allCallParticipants = useMemo(() => {
      const map = getCallParticipantsMap(
        allSessionParticipantsMap,
        realtimeMembersMap,
      );
      if (testCallParticipant) {
        map[testCallParticipant.id] = testCallParticipant;
      }
      return Object.values(map);
    }, [allSessionParticipantsMap, realtimeMembersMap, testCallParticipant]);

    const screenSharingParticipant = callParticipants.find(
      (participant) => participant.screenElement,
    );

    return {
      callParticipantsMap: callParticipantsMapWithTestPatient,
      callParticipants,
      allCallParticipants,
      screenSharingParticipant,
    };
  };

const getCallParticipantsMap = (
  participantsMap: Record<string, SessionParticipant>,
  membersMap: Record<string, RealtimeMember>,
): Record<string, ProviderViewCallParticipant> =>
  Object.fromEntries(
    Object.entries(participantsMap).map(([id, participant]) => {
      const member = membersMap[id];

      return [
        id,
        {
          ...member,
          ...participant,
          username: member?.state?.username,
          chatUserId: getChatUserIdFromIdentity(member?.identity),
        } as ProviderViewCallParticipant,
      ];
    }),
  );
